<script lang="ts" setup>
import { SelectField } from '@vue-interface/select-field';
import type { MgDataVariable } from 'types';
import { computed, watchEffect } from 'vue';

const props = defineProps<{
    id: string,
    name: string,
    item: MgDataVariable,
    index: number,
    errors: Record<string, string>,
    modelValue?: string[]|string
}>();

const emit = defineEmits<{
    'update:modelValue': [value: string[]],
    'update:formattedValue': [value: string],
    'update:readableValue': [value: string[]]
}>();

const model = computed({
    get() {
        if(!props.modelValue) {
            return [];
        }

        if(Array.isArray(props.modelValue)) {
            return props.modelValue;
        }
        
        return [props.modelValue];
    },
    set(value) {
        emit('update:modelValue', value);
    }
});

const isDefault = computed(() => {
    return JSON.stringify(model.value) === JSON.stringify([props.item.defaultValue]);
});

watchEffect(() => {
    if(isDefault.value && props.item.defaultValue) {
        emit('update:formattedValue', props.item.defaultValue);
        emit('update:readableValue', [props.item.defaultLabel || props.item.defaultValue]);
        return;
    }
    
    emit('update:formattedValue', model.value.map(value => `'${value}'`).join(', '));
    
    emit('update:readableValue', model.value.map(value => {
        return props.item.options?.find(i => i.value === value)?.label || value;
    }));
});

</script>

<template>
    <div class="flex items-center gap-2">
        <SelectField
            :id="id"
            v-model="model"
            multiple
            :name="name"
            :error="errors[name]"
            class="w-full">
            <option
                :value="item.defaultValue">
                {{ item.defaultLabel }}
            </option>
            <option
                v-for="{value, label} in item.options"
                :key="value"
                :value="value">
                {{ label }}
            </option>
        </SelectField>
        <slot
            v-if="!isDefault"
            name="reset" />
    </div>
</template>